"use client";

import { useState } from "react";

/**
 * One-question plan finder for the pricing page.
 *
 * Replaces the old interactive credit calculator (which asked visitors
 * to configure four inputs to compute a number they mostly already
 * sense). This asks how often they audit and maps the answer to a plan.
 *
 * The mapping deliberately steers toward Pro: anything short of daily /
 * client-scale work lands on Pro, because even occasional audits want
 * the full picture (desktop + mobile + AI) that Free can't give. Free
 * isn't surfaced as a finder result on purpose — it's still offered in
 * the hero trust line and the plan cards below for anyone who wants it.
 * Rationale copy lives per-option so the two Pro answers read
 * differently.
 */

type CadenceKey = "occasional" | "weekly" | "daily";
type PlanKey = "pro" | "agency";

const OPTIONS: Array<{
  key: CadenceKey;
  label: string;
  meta: string;
  plan: PlanKey;
  rationale: string;
}> = [
  {
    key: "occasional",
    label: "A few times a month",
    meta: "Quality checks that matter",
    plan: "pro",
    rationale:
      "Even a handful of audits a month deserve the full picture — desktop and mobile, plus AI to click through hidden states before you review. Pro covers it with credits to spare.",
  },
  {
    key: "weekly",
    label: "Every week",
    meta: "Staying on top of UX",
    plan: "pro",
    rationale:
      "Weekly audits across desktop and mobile sit comfortably inside Pro's 100 monthly credits — with AI assistance whenever you want it.",
  },
  {
    key: "daily",
    label: "Most days, or for clients",
    meta: "High volume",
    plan: "agency",
    rationale:
      "Daily, multi-page, multi-client work runs on Agency's 600 monthly credits, with top-up packs for the busiest stretches.",
  },
];

const PLAN_DETAILS: Record<PlanKey, { name: string; price: string; cta: string }> = {
  pro: { name: "Pro", price: "$19 / month", cta: "Get Pro" },
  agency: { name: "Agency", price: "$99 / month", cta: "Get Agency" },
};

export function PlanFinder() {
  // Default to the common case (weekly → Pro) so the result panel is
  // populated on first paint rather than empty.
  const [cadence, setCadence] = useState<CadenceKey>("weekly");

  const choice = OPTIONS.find((o) => o.key === cadence) ?? OPTIONS[1];
  const plan = PLAN_DETAILS[choice.plan];
  const popular = choice.plan === "pro";

  return (
    <section className="pricing-finder" aria-labelledby="pricing-finder-heading">
      <div className="pricing-finder-head">
        <p className="lp-kicker">Find your plan</p>
        <h2 id="pricing-finder-heading">Not sure which plan? One question.</h2>
        <p className="pricing-finder-sub">
          Pick the closest — you can switch plans any time.
        </p>
      </div>

      <div className="pricing-finder-grid">
        {/* ── Question ─────────────────────────────────── */}
        <div className="pricing-finder-question">
          <span className="pricing-finder-label" id="pricing-finder-q">
            How often will you run audits?
          </span>
          <div
            className="pricing-finder-options"
            role="radiogroup"
            aria-labelledby="pricing-finder-q"
          >
            {OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                role="radio"
                aria-checked={cadence === opt.key}
                className="pricing-finder-option"
                data-active={cadence === opt.key}
                onClick={() => setCadence(opt.key)}
              >
                <span className="pricing-finder-option-label">{opt.label}</span>
                <span className="pricing-finder-option-meta">{opt.meta}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Result ───────────────────────────────────── */}
        <aside className="pricing-finder-result" aria-live="polite">
          <p className="pricing-finder-result-kicker">
            {popular ? "Recommended · most popular" : "Recommended plan"}
          </p>
          <div className="pricing-finder-answer">
            <span className="pricing-finder-answer-name">{plan.name}</span>
            <span className="pricing-finder-answer-price">{plan.price}</span>
          </div>
          <p className="pricing-finder-rationale">{choice.rationale}</p>
          <a
            className="lp-btn lp-btn-primary pricing-finder-cta"
            href={`#plan-${choice.plan}`}
          >
            {plan.cta}
          </a>
        </aside>
      </div>
    </section>
  );
}
